import type {
  DishTag,
  Menu,
  RichIngredient,
  TaxonomyAllergen,
  TaxonomyPreference,
} from "api-types";

import type { MenuFilterState } from "./filter-state-type";
import {
  extractIngredientsFromMenu,
  extractTagsFromMenu,
} from "./menu-filtering-options";
import { NoPreference, UserPreference } from "./user-preference";

export type FilterReducerActions =
  | { type: "toggleAllergen"; allergen: TaxonomyAllergen }
  | { type: "togglePreference"; preference: TaxonomyPreference }
  | { type: "toggleIngredient"; ingredient: RichIngredient }
  | { type: "toggleTag"; tag: DishTag }
  | { type: "toggleShowMayContainAllergens" }
  | { type: "toggleShowCalories" }
  | { type: "toggleAllergenDetails" }
  | { type: "clearAllIngredients" }
  | { type: "clearAll" };

/**
 * Constructor of MenuFilterState when ingredients are sourced from a Menu.
 * This is used for storybooks and testing only.
 */
export const createInitialFilterStateFromMenu = (
  allergens: TaxonomyAllergen[],
  preferences: TaxonomyPreference[],
  menu: Menu | null, // null = restaurant with no menu.
  userPreference: UserPreference
): MenuFilterState =>
  createInitialFilterState({
    allergens,
    preferences,
    ingredients: menu ? extractIngredientsFromMenu(allergens, menu) : [],
    tags: menu ? extractTagsFromMenu(menu) : [],
    userPreference,
  });

// Initial state of the filter
// Can be constructed independent of the menu.
export const createInitialFilterState = ({
  allergens,
  preferences,
  ingredients,
  tags,
  userPreference,
}: {
  allergens: TaxonomyAllergen[];
  preferences: TaxonomyPreference[];
  ingredients: RichIngredient[];
  tags: DishTag[];
  userPreference: UserPreference;
}): MenuFilterState => {
  const preloadAllergenIds = new Set(userPreference.allergenIds);
  const preloadIngredientIds = new Set(userPreference.ingredientIds);
  const preloadTagIds = new Set(userPreference.tagIds);

  const allAllergens = allergens.map((allergen) => ({
    ...allergen,
    selected: preloadAllergenIds.has(allergen.igfdTaxAllergenId),
  }));

  const allPreferences = preferences.map((preference) => ({
    ...preference,
    selected: userPreference.preferenceId === preference.igfdTaxPreferenceId,
  }));
  const allTags = tags.map((tag) => ({
    ...tag,
    selected: preloadTagIds.has(tag.id),
  }));

  // Step 0: blank state of ingredients
  const allIngredients = ingredients.map((ingredient) => ({
    ...ingredient,
    enabled: true,
    selectedReason: null,
    selected: false,
  }));

  const updatedIngredientState: MenuFilterState["ingredients"] =
    // Step 1: Mark the ingredients that contain the allergen any of the currently selected allergens
    // as disabled, indicating all the allergens contributing to the disabling reason.
    allIngredients
      .map((i) => computeIngredientFilterState(i, preloadAllergenIds))
      // Step 2: now mark user selected ingredients as selected
      .map((ingredient) =>
        // enabled means an allergen is not automatically making this unselectable.
        ingredient.enabled &&
        preloadIngredientIds.has(ingredient.igfdTaxIngredientId)
          ? {
              ...ingredient,
              selected: true,
              selectedReason: null,
            }
          : ingredient
      );

  return {
    allergens: allAllergens,
    selectedAllergens: allAllergens.filter((allergen) => allergen.selected),
    preferences: allPreferences,
    selectedPreference:
      preferences.find(
        (preference) =>
          preference.igfdTaxPreferenceId === userPreference.preferenceId
      ) || null,
    ingredients: updatedIngredientState,
    selectedIngredients: updatedIngredientState.filter(
      (ingredient) => ingredient.selected
    ),
    tags: allTags,
    selectedTags: allTags.filter((tag) => tag.selected),
    showMayContainAllergens: userPreference.showMayContainAllergens,
    showCalories: userPreference.showCalories,
    showAllergenDetails: userPreference.showAllergenDetails,
  };
};

/**
 * Reducer for the filter state (to be used in react reducer)
 */
export const filterMenuReducer = (
  state: MenuFilterState,
  action: FilterReducerActions
): MenuFilterState => {
  switch (action.type) {
    case "toggleAllergen":
      const { allergen: toggledAllergen } = action;
      // is this a select operation or a deselect operation
      const updatedAllergens = state.allergens.map((allergen) =>
        allergen.igfdTaxAllergenId === toggledAllergen.igfdTaxAllergenId
          ? { ...allergen, selected: !allergen.selected }
          : allergen
      );

      // After the toggle has been done, find the selected allergens
      const selectedAllergenIds = new Set(
        updatedAllergens
          .filter((allergen) => allergen.selected)
          .map((allergen) => allergen.igfdTaxAllergenId)
      );

      // Mark the ingredients that contain the allergen any of the currently selected allergens
      // as disabled, indicating all the allergens contributing to the disabling reason.
      const updatedIngredientState: MenuFilterState["ingredients"] =
        state.ingredients.map((i) =>
          computeIngredientFilterState(i, selectedAllergenIds)
        );

      return {
        ...state,
        allergens: updatedAllergens,
        selectedAllergens: updatedAllergens.filter(
          (allergen) => allergen.selected
        ),
        ingredients: updatedIngredientState,
        selectedIngredients: updatedIngredientState.filter((i) => i.selected),
      };
    case "togglePreference":
      const prevPreference = state.selectedPreference;
      const { preference: toggledPreference } = action;
      return {
        ...state,
        preferences: state.preferences.map((preference) => ({
          ...preference,
          selected:
            preference.igfdTaxPreferenceId ===
            toggledPreference.igfdTaxPreferenceId
              ? !preference.selected
              : false,
        })),
        selectedPreference:
          prevPreference?.igfdTaxPreferenceId ===
          toggledPreference.igfdTaxPreferenceId
            ? null
            : toggledPreference,
      };
    case "toggleIngredient":
      const { ingredient: toggledIngredient } = action;
      const previousIngredient = state.ingredients.find(
        (ingredient) =>
          ingredient.igfdTaxIngredientId ===
          toggledIngredient.igfdTaxIngredientId
      );

      if (!previousIngredient) {
        // the ingredient doesn't exist in state, add it.
        const ingredientToAdd: MenuFilterState["ingredients"][0] = {
          ...toggledIngredient,
          enabled: true,
          selected: true,
          selectedReason: null,
        };
        return {
          ...state,
          ingredients: [...state.ingredients, ingredientToAdd],
          selectedIngredients: [...state.selectedIngredients, ingredientToAdd],
        };
      }

      // the ingredient already happens to exist.

      // if the toggled ingredient is disabled, do nothing
      if (!previousIngredient.enabled) {
        return state;
      }

      const updatedIngredients: MenuFilterState["ingredients"] =
        state.ingredients.map((ingredient) =>
          ingredient.igfdTaxIngredientId ===
          previousIngredient.igfdTaxIngredientId
            ? {
                ...ingredient,
                selected: !ingredient.selected,
                selectedReason: null,
              }
            : ingredient
        );

      return {
        ...state,
        ingredients: updatedIngredients,
        selectedIngredients: updatedIngredients.filter(
          (ingredient) => ingredient.selected
        ),
      };
    case "toggleTag":
      const { tag: toggledTag } = action;
      const selectedTags = state.tags.map((tag) =>
        tag.id === toggledTag.id ? { ...tag, selected: !tag.selected } : tag
      );
      return {
        ...state,
        tags: state.tags.map((tag) =>
          tag.id === toggledTag.id ? { ...tag, selected: !tag.selected } : tag
        ),
        selectedTags: selectedTags.filter((a) => a.selected),
      };
    case "toggleShowMayContainAllergens":
      return {
        ...state,
        showMayContainAllergens: !state.showMayContainAllergens,
      };
    case "toggleShowCalories":
      return {
        ...state,
        showCalories: !state.showCalories,
      };
    case "toggleAllergenDetails":
      return {
        ...state,
        showAllergenDetails: !state.showAllergenDetails,
      };
    case "clearAllIngredients":
      return {
        ...state,
        ingredients: state.ingredients.map((ingredient) => ({
          ...ingredient,
          selected: false,
          enabled: true,
          selectedReason: null,
        })),
        selectedIngredients: [],
      };
    case "clearAll":
      return {
        allergens: state.allergens.map((allergen) => ({
          ...allergen,
          selected: false,
        })),
        selectedAllergens: [],
        preferences: state.preferences.map((preference) => ({
          ...preference,
          selected: false,
        })),
        selectedPreference: null,
        ingredients: state.ingredients.map((ingredient) => ({
          ...ingredient,
          selected: false,
          enabled: true,
          selectedReason: null,
        })),
        selectedIngredients: [],
        tags: state.tags.map((tag) => ({
          ...tag,
          selected: false,
        })),
        selectedTags: [],
        showMayContainAllergens: NoPreference.showMayContainAllergens,
        showCalories: NoPreference.showCalories,
        showAllergenDetails: NoPreference.showAllergenDetails,
      };
    default:
      return state;
  }
};

function computeIngredientFilterState(
  ingredient: MenuFilterState["ingredients"][0],
  selectedAllergenIds: Set<string>
): MenuFilterState["ingredients"][0] {
  const containsSelectedAllergens = ingredient.allergens.filter((allergen) =>
    selectedAllergenIds.has(allergen.igfdTaxAllergenId)
  );
  return {
    ...ingredient,
    enabled: containsSelectedAllergens.length === 0,
    // if the ingredient is disabled, it cannot be selected.
    selected: containsSelectedAllergens.length === 0 && ingredient.selected,
    selectedReason:
      containsSelectedAllergens.length > 0
        ? {
            reason: "contains-allergen",
            containsSelectedAllergens,
          }
        : null,
  };
}

/** Returns true if the user has selected at least one filter. */
export const userHasSelectedFilters = (state: MenuFilterState): boolean =>
  state.selectedAllergens.length > 0 ||
  state.selectedPreference !== null ||
  state.selectedIngredients.length > 0 ||
  state.selectedTags.length > 0 ||
  state.showMayContainAllergens != NoPreference.showMayContainAllergens ||
  state.showCalories != NoPreference.showCalories;
